import type { DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { observer } from 'mobx-react-lite'
import { DraftJobWizardContentHeaderContainer } from '@/features/jobs/drafting/components/DraftJobView/DraftJobWizardContentHeaderContainer'
import { useId } from 'react'
import { AnswerEditorBody } from '@/features/jobs/questions/components/AnswerEditorBody'
import type { RequestedProviderSelectionViewModel } from '@/features/jobs/drafting/requested-provider/RequestedProviderSelectionViewModel'
import { Choice, ChoiceList, IconDecoration } from '@/components/ChoiceList'
import { Icon } from '@/components/Icon'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useViewModelBackedAriaSingleSelection } from '@/hooks/aria-selection-hooks'
import { Avatar } from '@/components/Avatar'
import { useScopedViewModel } from '@/hooks/view-model-hooks'
import { observable } from 'mobx'
import { Id } from '@/infra/id'
import type { PreviousProvider } from '@/features/jobs/drafting/PreviousProvider'
import { WizardContentBody } from '@/components/Wizard'

interface RequestedProviderSelectionViewContainerProps {
  vm: DraftJobViewModel
}

/**
 * Requested Provider Selection View Container
 */
export const RequestedProviderSelectionViewContainer = observer(
  function RequestedProviderSelectionViewContainer({
    vm,
  }: RequestedProviderSelectionViewContainerProps) {
    const titleId = useId()
    return (
      <>
        <DraftJobWizardContentHeaderContainer
          vm={vm}
          title="Would you like to connect with a previous Pro?"
          isSkeleton={false}
          titleId={titleId}
          description={
            <>
              Reconnecting with your previous Pro is not guaranteed. It may take
              up to 24 hours, and if they’re unavailable, your job will be
              assigned to another qualified Pro.
            </>
          }
        />

        <WizardContentBody>
          <AnswerEditorBody>
            <RequestedProviderSelectorContainer
              aria-labelledby={titleId}
              isDisabled={vm.isReadOnly}
              vm={vm.requestedProviderSelectionViewModel}
            />
          </AnswerEditorBody>
        </WizardContentBody>
      </>
    )
  },
)

interface RequestedProviderSelectorContainerProps {
  vm: RequestedProviderSelectionViewModel
  isDisabled: boolean
  'aria-labelledby'?: string
}

/**
 * Requested Provider Selector Container
 */
export const RequestedProviderSelectorContainer = observer(
  function RequestedProviderSelectorContainer({
    vm,
    isDisabled,
    ...rest
  }: RequestedProviderSelectorContainerProps) {
    const isSpacious = useIsSpacious()

    // Intermediate VM that translates to the hook's necessary format.
    // This is simpler than polluting the VM with React Aria-specific cruft.
    const selectionVm = useScopedViewModel(() =>
      observable({
        get allKeys(): string[] {
          return this.options.map((o) => o.id)
        },
        get options(): RequestedProviderSelectionOption[] {
          return [
            {
              id: 'any-available',
              type: 'AnyAvailable',
            },
            ...vm.previousProviders.map(
              (p): ProviderOption => ({
                type: 'Provider',
                provider: p,
                id: Id.assert(p.userId),
              }),
            ),
          ]
        },
        get selectedItem(): RequestedProviderSelectionOption | null {
          const selection = vm.selection
          const options = this.options
          if (selection === null) {
            return null
          }

          if (selection.type === 'AnyChampionProvider') {
            return options.find((i) => i.type === 'AnyAvailable') ?? null
          }

          return (
            options.find(
              (i) =>
                i.type === 'Provider' &&
                i.provider.userId === selection.providerUserId,
            ) ?? null
          )
        },
        onSelectionChange(option: RequestedProviderSelectionOption) {
          if (option.type === 'AnyAvailable') {
            vm.selectAnyProvider()
          } else {
            vm.selectProvider(option.provider)
          }
        },
      }),
    )

    const selectionProps = useViewModelBackedAriaSingleSelection({
      disallowEmptySelection: true,
      items: selectionVm.options,
      selectedItem: selectionVm.selectedItem,
      getKey: (i) => i.id,
      onSelectionChange: selectionVm.onSelectionChange,
    })

    return (
      <ChoiceList
        items={selectionVm.options}
        layout={isSpacious ? 'grid' : 'stack'}
        disabledKeys={isDisabled ? selectionVm.allKeys : undefined}
        testId="requested-provider-selection"
        {...selectionProps}
        {...rest}
      >
        {(option) => (
          <ProviderSelectionAnswerOptionContainer
            id={option.id}
            option={option}
          />
        )}
      </ChoiceList>
    )
  },
)

interface ProviderSelectionAnswerOptionContainerProps {
  option: RequestedProviderSelectionOption
  id: string
}

/**
 * Provider Selection Answer Option Container
 */
export const ProviderSelectionAnswerOptionContainer = observer(
  function ProviderSelectionAnswerOptionContainer({
    option,
  }: ProviderSelectionAnswerOptionContainerProps) {
    if (option.type === 'AnyAvailable') {
      return (
        <Choice
          id={option.id}
          label="Match me with a new Pro"
          decoration={
            <IconDecoration boxed>
              <Icon name="user" />
            </IconDecoration>
          }
          testId="requested-provider-selection-option-any"
          description={
            <>We'll match you with a qualified Pro based on your situation.</>
          }
        />
      )
    }

    return (
      <Choice
        id={option.id}
        label={option.provider.displayName}
        decoration={<Avatar subject={option.provider} />}
        description={<>Worked on {option.provider.previousJobInfo.title}</>}
        testId={`requested-provider-selection-option-${option.provider.userId}`}
      />
    )
  },
)

/**
 * Options to select from.
 */
type RequestedProviderSelectionOption = ProviderOption | AnyAvailableOption

/**
 * A Provider option.
 */
interface ProviderOption {
  id: string
  type: 'Provider'
  provider: PreviousProvider
}

/**
 * Any available option.
 */
interface AnyAvailableOption {
  id: string
  type: 'AnyAvailable'
}
